/**
 * Wiring for snippets/footer-country-select.liquid. Deliberately not part of the portable
 * site-region-router unit (see that file's own top comment) — this piece is specific to this
 * repo's GTM/GA4 setup and would need re-theming (not a blind copy) if ported to another theme,
 * unlike assets/site-region-router.js.
 *
 * 2026-09-22: this file used to fork per branch — live/ofhunger-us hardcoded `select.value =
 * 'US'`, live/ofhunger-global read the current Market subpath instead — even though the two
 * behaviors don't actually conflict: na simply has no subpaths, so "the visitor's current
 * Market" and "US" were always the same answer there. Unified into one implementation by moving
 * that difference into data (`SITES.na.defaultCountry` in assets/site-region-router.js) rather
 * than two divergent files — `window.RegionRouter.currentMarket()` now resolves to 'US'
 * anywhere on us.ofhunger.com the same way it resolves to 'BR' on global.ofhunger.com/pt-br,
 * and to `null` (mapped to 'OTHER' below) on a site with neither a Market match nor a default
 * identity of its own (www, or global's own bare root).
 *
 * Does NOT rely on the generic data-analytics-event delegate in assets/analytics-bridge.js:
 * that delegate auto-reads a changed <select>'s raw `value` as the `switch_country` event's
 * `value` param, but this control's option values are an ISO country code or the "OTHER"
 * placeholder (see snippets/footer-country-select.liquid's own comment), not destination
 * URLs — and a GA4 custom dimension ("Switch Country Destination") is already provisioned
 * expecting a URL string there (project-context/tracking-plan-gtm-exec-v1.md). Dispatching
 * manually with the resolved destination keeps that contract intact.
 */
import { dispatch } from '@theme/analytics-bridge';

// Asks the shared router which country the current site represents for this visitor — a Market
// subpath match (BR/MX on global) or a site's own implicit identity (US on na, via
// SITES.na.defaultCountry) — falling back to 'OTHER' for a site with neither (www, global's own
// root) or if the router script failed to load for some reason.
function detectDefaultValue() {
  return (window.RegionRouter && window.RegionRouter.currentMarket()) || 'OTHER';
}

function init() {
  const select = document.querySelector('[data-country-select]');
  if (!select) return;

  select.value = detectDefaultValue();

  select.addEventListener('change', () => {
    const iso = select.value;
    if (!iso || !window.RegionRouter) return;

    const result = window.RegionRouter.applyRegion(iso);
    dispatch('switch_country', { value: result.redirected ? result.destinationUrl : '' });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
